import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SavedLead } from '../../../../../services/leadsService';
import { useTranslation } from 'react-i18next';
import CustomCheckbox from '../../../../../components/CustomCheckbox/CustomCheckbox';
import RatingStars from '../RatingStars/RatingStars';
import StatusBadge from '../../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/StatusBadge/StatusBadge';
import ProspectedBadge from '../../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/ProspectedBadge/ProspectedBadge';
import LeadDetailModal from '../../LeadDetailModal/LeadDetailModal';
import EditLeadModal from '../../EditLeadModal/EditLeadModal';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';

interface Lead extends SavedLead { }

interface LeadsMobileCardProps {
    lead: Lead;
    isSelected: boolean;
    onSelect: () => void;
    onDelete: (id: string) => void;
    onLeadUpdated: (updatedLead: SavedLead) => void;
}

const LeadsMobileCard: React.FC<LeadsMobileCardProps> = ({
    lead,
    isSelected,
    onSelect,
    onDelete,
    onLeadUpdated,
}) => {
    const { t } = useTranslation();
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleConfirmDelete = () => {
        onDelete(lead.id);
        setIsDeleteModalOpen(false);
    };

    return (
        <>
            <motion.div
                className={`p-4 rounded-lg border ${isSelected ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'} mb-3 shadow-sm active:scale-[0.99] transition-transform`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setIsViewModalOpen(true)}
                layout
            >
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div onClick={(e) => e.stopPropagation()}>
                            <CustomCheckbox checked={isSelected} onChange={onSelect} />
                        </div>
                        <div className="flex flex-col min-w-0">
                            <h3 className="font-bold text-[#181411] dark:text-white line-clamp-1 break-all text-base">{lead.company_name}</h3>
                            {lead.phone_number && (
                                <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{lead.phone_number}</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    {/* Location */}
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('leads.table_header_country')}</span>
                        <span className="font-medium dark:text-gray-300">{lead.location || 'N/A'}</span>
                    </div>

                    {/* Rating */}
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('leads.table_header_rating')}</span>
                        <RatingStars rating={lead.rating} />
                    </div>
                </div>

                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex gap-2">
                        <StatusBadge status={lead.whatsapp_status} whatsapp={lead.whatsapp_status === 'Validado'} />
                        <ProspectedBadge prospected={lead.prospected} />
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setIsEditModalOpen(true)}
                            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 transition-colors"
                            title={t('Editar')}
                        >
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                        <button
                            onClick={() => setIsViewModalOpen(true)}
                            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 transition-colors"
                            title={t('Visualizar')}
                        >
                            <span className="material-symbols-outlined text-[20px]">visibility</span>
                        </button>
                        <button
                            onClick={() => setIsDeleteModalOpen(true)}
                            className="p-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title={t('dashboard.action_delete')}
                        >
                            <span className="material-symbols-outlined text-[20px]">delete</span>
                        </button>
                    </div>
                </div>
            </motion.div>

            <LeadDetailModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                lead={lead}
            />

            <EditLeadModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                lead={lead}
                onLeadUpdated={onLeadUpdated}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title={t('modals.confirm_delete_title')}
                message={t('Você tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita.')}
                confirmText={t('Sim, Excluir')}
                cancelText={t('modals.cancel')}
            />
        </>
    );


};

export default LeadsMobileCard;
